import { defineStore } from 'pinia'
import { getPrimarySaju, setPrimarySaju as setPrimarySajuAPI } from '@/api/UserGroup'
import { getUserInfoAPI } from '@/api/myPage'

const PRIMARY_SAJU_CACHE_KEY = 'primary-saju-cache'

const readCache = () => {
  try {
    const userId = sessionStorage.getItem('authUserId')
    if (!userId) return null
    const raw = sessionStorage.getItem(PRIMARY_SAJU_CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (parsed.userId !== userId) return null
    return parsed
  } catch {
    return null
  }
}

const writeCache = (primarySaju, mySaju) => {
  try {
    const userId = sessionStorage.getItem('authUserId')
    if (!userId) return
    sessionStorage.setItem(
      PRIMARY_SAJU_CACHE_KEY,
      JSON.stringify({ userId, primarySaju, mySaju }),
    )
  } catch (e) {
    console.warn('Failed to save primary saju cache', e)
  }
}

export const usePrimarySajuStore = defineStore('primarySaju', () => {
  const _cache = readCache()

  /** 대표사주 정보 */
  const primarySaju = ref(_cache?.primarySaju ?? null)

  /** 대표사주 ID */
  const primarySajuId = computed(() => primarySaju.value?.guestUserId || null)

  /** 내 사주 정보 */
  const mySaju = ref(_cache?.mySaju ?? null)

  /** 내 사주 ID */
  const mySajuId = computed(() => mySaju.value?.guestUserId || null)

  /** 로딩 상태 */
  const isLoading = ref(false)

  /** 대표사주 조회 */
  const fetchPrimarySaju = async () => {
    isLoading.value = true
    try {
      const data = await getPrimarySaju()
      primarySaju.value = data
      writeCache(primarySaju.value, mySaju.value)
      return data
    } catch (e) {
      // 404 등 대표사주가 없는 경우
      primarySaju.value = null
      writeCache(primarySaju.value, mySaju.value)
      return null
    } finally {
      isLoading.value = false
    }
  }

  /** 내 사주 조회 (유저 정보 API에서 mySaju 추출) */
  const fetchMySaju = async () => {
    try {
      const userInfo = await getUserInfoAPI()
      mySaju.value = userInfo?.mySaju || null
      writeCache(primarySaju.value, mySaju.value)
      return mySaju.value
    } catch (e) {
      mySaju.value = null
      writeCache(primarySaju.value, mySaju.value)
      return null
    }
  }

  /** 대표사주 설정 */
  const setPrimarySaju = async (guestUserId) => {
    isLoading.value = true
    try {
      await setPrimarySajuAPI(guestUserId)
      // 설정 후 다시 조회하여 최신 정보 반영
      await fetchPrimarySaju()
      return true
    } catch (e) {
      console.error('대표사주 설정 실패:', e)
      return false
    } finally {
      isLoading.value = false
    }
  }

  /** 대표사주 여부 확인 */
  const isPrimarySaju = (guestUserId) => {
    return primarySajuId.value === guestUserId
  }

  /** 내 사주 여부 확인 */
  const isMySaju = (guestUserId) => {
    return mySajuId.value === guestUserId
  }

  /** 초기화 */
  const reset = () => {
    primarySaju.value = null
    mySaju.value = null
    try {
      sessionStorage.removeItem(PRIMARY_SAJU_CACHE_KEY)
    } catch {}
  }

  /** 대표사주 + 내 사주 일괄 조회 */
  const fetchPrimaryAndMySaju = async () => {
    await Promise.all([fetchPrimarySaju(), fetchMySaju()])
  }

  return {
    primarySaju,
    primarySajuId,
    mySaju,
    mySajuId,
    isLoading,
    fetchPrimarySaju,
    fetchMySaju,
    fetchPrimaryAndMySaju,
    setPrimarySaju,
    isPrimarySaju,
    isMySaju,
    reset,
  }
})
